import { Badge, Banner, BlockStack, Box, InlineGrid, InlineStack, Link, Text } from '@shopify/polaris';
import { useState } from 'react';
import ApprovalStatusBadge from '../../../shared/components/approvals/ApprovalStatusBadge';
import { DOC_TYPE_META } from '../../../shared/components/approvals/approvalsApi';
import { formatDate, formatToCurrency } from '../../../shared/components/functions';

const Fact = ({ label, children }) => (
  <BlockStack gap="050">
    <Text variant="bodySm" tone="subdued">
      {label}
    </Text>
    <Text variant="bodyMd">{children ?? '-'}</Text>
  </BlockStack>
);

// Condensed facts of the document: who, what, how much, when, plus the AI memo and duplicate warnings.
export default function DocumentHeader({ card, showAssignment = true }) {
  const [memoOpen, setMemoOpen] = useState(false);
  const meta = DOC_TYPE_META[card.docType];
  const doc = card.document;
  const myLines = card.lines.filter((line) => card.myPendingLineIds.includes(line.id));
  const assignedBy = myLines.find((line) => line.assignedByAfkorting);
  const memo = doc.memo || '';
  const memoLong = memo.length > 160;

  return (
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="start" wrap>
        <BlockStack gap="100">
          <InlineStack gap="200" blockAlign="center" wrap>
            <Text variant="headingLg" as="h2">
              {card.supplier?.NAAM || doc.Leveranciernaam || 'Unknown supplier'}
            </Text>
            {card.supplier ? (
              <Link url={`/companies/${card.supplier.FIRNR}`} removeUnderline>
                open supplier
              </Link>
            ) : (
              <Badge tone="attention">Supplier not set</Badge>
            )}
          </InlineStack>
          <InlineStack gap="200" blockAlign="center" wrap>
            <Link url={`${meta.detailPath}/${doc[meta.pk]}`} removeUnderline>
              {`${meta.label} ${doc[meta.numberField]}`}
            </Link>
            {doc.faktuurref ? <Text tone="subdued">ref {doc.faktuurref}</Text> : null}
            {doc.PeppolStatus ? <Badge tone="info">Peppol</Badge> : <Badge>PDF</Badge>}
            {doc.processed ? null : <Badge tone="attention">Not processed</Badge>}
            {doc.status ? <Badge tone="success">Paid</Badge> : null}
            <ApprovalStatusBadge status={doc.ApprovalStatus} assigneeAfkorting={doc.assigneeAfkorting} lines={card.lines} />
          </InlineStack>
        </BlockStack>
        <Box background="bg-fill-info" padding="200" borderRadius="200">
          <BlockStack gap="050" inlineAlign="end">
            <Text variant="headingLg" fontWeight="bold">
              {formatToCurrency(card.allocation.basis ?? doc.Bedragfaktuur)}
            </Text>
            <Text variant="bodySm" tone="subdued">
              excl. VAT
              {doc.Bedragfaktuur != null && card.allocation.basis !== null && Math.abs(doc.Bedragfaktuur - card.allocation.basis) > 0.005 ? ` · ${formatToCurrency(doc.Bedragfaktuur)} incl.` : ''}
            </Text>
          </BlockStack>
        </Box>
      </InlineStack>

      <InlineGrid columns={{ xs: 2, md: 4 }} gap="300">
        <Fact label="Document date">{doc.datum ? formatDate(doc.datum) : '-'}</Fact>
        <Fact label="Due date">{doc.datumbetaling ? formatDate(doc.datumbetaling) : '-'}</Fact>
        <Fact label="Received">{doc.datumin ? formatDate(doc.datumin) : '-'}</Fact>
        <Fact label="Open amount">{doc.bedragopen != null ? formatToCurrency(doc.bedragopen) : '-'}</Fact>
      </InlineGrid>

      {memo ? (
        <Box background="bg-surface-secondary" padding="200" borderRadius="200">
          <Text variant="bodySm" tone="subdued">
            Content
          </Text>
          <Text as="p">{memoLong && !memoOpen ? `${memo.slice(0, 160)}…` : memo}</Text>
          {memoLong ? (
            <Link removeUnderline onClick={() => setMemoOpen((v) => !v)}>
              {memoOpen ? 'less' : 'more'}
            </Link>
          ) : null}
        </Box>
      ) : null}

      {showAssignment && assignedBy ? (
        <Text variant="bodySm" tone="subdued">
          Assigned to you by <strong>{assignedBy.assignedByAfkorting}</strong>
          {assignedBy.AssignedAt ? ` on ${formatDate(assignedBy.AssignedAt)}` : ''}
          {myLines.find((line) => line.Notitie)?.Notitie ? ` · note: ${myLines.find((line) => line.Notitie).Notitie}` : ''}
        </Text>
      ) : null}

      {card.duplicateCandidates?.length ? (
        <Banner tone="warning" title="Possible duplicate">
          <InlineStack gap="200" wrap>
            {card.duplicateCandidates.map((dup) => (
              <Link key={dup.id} url={`${meta.detailPath}/${dup.id}`} removeUnderline>
                {`${meta.label} ${dup.number} · ${formatDate(dup.datum)} · ${formatToCurrency(dup.Bedragfaktuur)}${dup.faktuurref ? ` · ref ${dup.faktuurref}` : ''}${dup.paid ? ' · paid' : ''}`}
              </Link>
            ))}
          </InlineStack>
        </Banner>
      ) : null}
    </BlockStack>
  );
}
